import { useEffect, useRef, useState } from "react";
import { getAppointmentPaymentHistory, post_ProcessPaymentByOffline, post_ProcessSquareTerminalPayment } from "../../../services/Payment";
import useAuth from "../../../hook/useAuth";
import { post_CreateWorkOrderForAppointment } from "../../../services/WorkOrder";
import { getStatus } from "../../../services/Appointment";
import { TerminalPleaseWaitPopup } from "../../../components/TerminalPleaseWaitPopup";
import useConfirm from "../../../hook/useConfirm";
import axios from "axios";
import { TerminalPaymentSuccessPopup } from "../../../components/TerminalPaymentSuccessPopup";
import { baseURL } from "../../../config/api";

type PaymentsProps = {
    primaryKey: null | number;
    setPleaseWaitPopup: (value: boolean) => void;
    invoiceData: (null | any);
    updateAppointmentPayment: () => void;
    goToNextPage: () => void;
    appointmentData: any
}

const paymentMethodOptions = [
    {
        value: 0,
        label: "Select Payment Method"
    },
    {
        value: 1,
        label: "Credit Card"
    },
    {
        value: 2,
        label: "Bank Transfer"
    },
    {
        value: 3,
        label: "Cash"
    },
]

const TOTAL_TIME = 120;
const POLL_INTERVAL = 4000;

export const Payments = ({ primaryKey, setPleaseWaitPopup, invoiceData, updateAppointmentPayment, goToNextPage, appointmentData }: PaymentsProps) => {
    const { auth }: any = useAuth();
    const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
    const [workOrderStatus, setWorkOrderStatus] = useState<any[]>([]);
    const [paymentHistory, setPaymentHistory] = useState<any[]>([]);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<number>(0);
    const [reference, setReference] = useState<string>("");
    const [paymentHistoryRefresh, setPaymentHistoryRefresh] = useState<boolean>(true);
    const [terminalPleaseWaitPopup, setTerminalPleaseWaitPopup] = useState<boolean>(false);
    const [terminalPaymentSuccessPopup, setTerminalPaymentSuccessPopup] = useState<boolean>(false);
    const [terminalPaymentInvoiceNo, setTerminalPaymentInvoiceNo] = useState<string>("");
    const [timeLeft, setTimeLeft] = useState(TOTAL_TIME);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const pollRef = useRef<NodeJS.Timeout | null>(null);
    const autoCloseRef = useRef<NodeJS.Timeout | null>(null);


    useEffect(() => {
        getStatus("WorkOrder")
            .then((data) => {
                setWorkOrderStatus(() => data?.map((status: any) => {
                    return {
                        id: status.statusID,
                        statusValue: status.statusValue
                    }
                }))
            })
            .catch((error) => {
                console.error(error)
            })
    }, [])


    useEffect(() => {
        if (primaryKey && primaryKey > 0 && paymentHistoryRefresh) {
            setPleaseWaitPopup(true);
            getAppointmentPaymentHistory(primaryKey)
                .then((data) => {
                    setPaymentHistory(data);
                    setPleaseWaitPopup(false);
                    setPaymentHistoryRefresh(false);
                })
                .catch((error) => {
                    console.error(error)
                })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [primaryKey, paymentHistoryRefresh])

    useEffect(() => {
        return () => stopAll();
    }, []);


    const stopAll = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }

        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    };

    const startPaymentProcess = (invoiceNumber: string) => {
        setTerminalPleaseWaitPopup(true);
        setTimeLeft(TOTAL_TIME);

        // TIMER (1 sec)
        timerRef.current = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 1) {
                    stopAll();
                    setTerminalPleaseWaitPopup(false);
                    setConfirmData({
                        title: "Timeout",
                        summary: "Payment timed out try again",
                        confirmBtn: "Ok",
                        isConfirm: (cb: any) => cb(),
                    });

                    setConfirmPopup(true);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        pollRef.current = setInterval(async () => {
            try {
                const res = await checkTransaction(invoiceNumber);

                if (res?.status === "Paid") {
                    stopAll();
                    setTerminalPleaseWaitPopup(false);
                    setTerminalPaymentSuccessPopup(true);
                    autoCloseRef.current = setTimeout(() => {
                        setTerminalPaymentSuccessPopup(false);
                        createWorkOrder();
                        if (autoCloseRef.current) {
                            clearTimeout(autoCloseRef.current);
                            autoCloseRef.current = null;
                        }
                    }, 3000);


                }

                if (res?.status === "Failed") {
                    stopAll();
                    setTerminalPleaseWaitPopup(false);
                    setConfirmData({
                        title: "Failed",
                        summary: res?.statusDescription,
                        confirmBtn: "Ok",
                        isConfirm: (cb: any) => cb(),
                    });

                    setConfirmPopup(true);
                }


                if (res?.status === "Canceled") {
                    stopAll();
                    setTerminalPleaseWaitPopup(false);
                    setConfirmData({
                        title: "Canceled",
                        summary: res?.statusDescription,
                        confirmBtn: "Ok",
                        isConfirm: (cb: any) => cb(),
                    });

                    setConfirmPopup(true);
                }
            } catch (err) {
                console.error(err);
            }
        }, POLL_INTERVAL);
    };

    const handleCancel = () => {
        stopAll();
        setTerminalPleaseWaitPopup(false);
        setPleaseWaitPopup(true);
        axios.post(`${baseURL}payments/CancelTerminalPayment/${terminalPaymentInvoiceNo}`)
            .then((res) => {
                setPleaseWaitPopup(false);
            })
            .catch((erorr) => {
                console.error(erorr);
            })
    };

    const checkTransaction = async (invoiceNumber: string) => {
        const res = await fetch(`${baseURL}transactions/GetTerminalByReference${invoiceNumber}`);
        return res.json();
    };


    const handleCreditCardPayment = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setPleaseWaitPopup(true);
        post_ProcessSquareTerminalPayment(Number(primaryKey), invoiceData?.totalAmount, auth?.id)
            .then((data) => {
                setPleaseWaitPopup(false);
                startPaymentProcess(data?.invoiceNumber);
                setTerminalPaymentInvoiceNo(data?.invoiceNumber);
            })
            .catch((error) => {
                console.error(error)
            })

    }

    const handleOfflinePayment = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setPleaseWaitPopup(true);
        post_ProcessPaymentByOffline({
            appointmentId: primaryKey,
            amount: invoiceData?.totalAmount,
            paymentMethod: selectedPaymentMethod === 2 ? "Bank Transfer" : "Cash",
            reference
        }, auth?.id)
            .then((res) => {
                createWorkOrder();
            })
            .catch((error) => {
                console.error(error)
            })
    }

    const createWorkOrder = () => {
        const statusID = workOrderStatus?.filter((s: any) => s?.statusValue === "Queued")[0]?.id
        if (primaryKey) {
            setPleaseWaitPopup(true);
            post_CreateWorkOrderForAppointment(Number(primaryKey), statusID, auth?.id)
                .then((id) => {
                    updateAppointmentPayment();
                    setPaymentHistoryRefresh(true);
                })
                .catch((error) => {
                    console.error(error)
                })
        }
    }

    return (
        <>
            <div className="w-full mt-2">
                <div className={`w-full pb-4`}>
                    {
                        invoiceData && invoiceData?.status === "Unpaid" && (
                            <div className="flex">
                                <div className="mt-2 p-2 w-[50%]">

                                    <div>
                                        <span className="text-sm w-full inline-block font-semibold text-gray-800 border-b pb-2">
                                            Price Detials
                                        </span>
                                        <div className="w-full border rounded-lg p-4 space-y-2 text-sm mt-3">
                                            <SummaryRow label="Subtotal" value={invoiceData?.subTotal} />
                                            <SummaryRow label="Tax" value={invoiceData?.taxAmount} />
                                            <SummaryRow label="Total" value={invoiceData?.totalAmount} bold />
                                        </div>
                                    </div>
                                    <div className="mt-3">
                                        <span className="text-sm w-full inline-block font-semibold text-gray-800 border-b pb-2">
                                            Payment Method - Credit Card/Bank Transfer/Cash
                                        </span>

                                        <select
                                            value={selectedPaymentMethod}
                                            onChange={(e) => {
                                                setSelectedPaymentMethod(Number(e.target.value))
                                            }}
                                            className="mt-2 w-[400px] border border-gray-300 rounded-md px-3 py-2 text-sm outline-none focus:border-primary bg-white"
                                        >
                                            {
                                                paymentMethodOptions.map((ops: any, ind: number) => (
                                                    <option key={ind} value={ops?.value}>
                                                        {ops?.label}
                                                    </option>
                                                ))
                                            }
                                        </select>

                                        {
                                            selectedPaymentMethod === 1 && (
                                                <>
                                                    <form className="py-2 w-full" onSubmit={handleCreditCardPayment}>
                                                        <button type="submit" className="w-[400px] mt-4 border bg-primary hover:bg-white hover:border-primary hover:text-primary text-white text-sm py-2 px-8 rounded-[70px] font-semibold transition shadow-md">
                                                            Pay
                                                        </button>
                                                    </form>
                                                </>
                                            )
                                        }


                                        {
                                            (selectedPaymentMethod === 2 || selectedPaymentMethod === 3) && (
                                                <form className="py-2 w-full" onSubmit={handleOfflinePayment}>
                                                    <div>
                                                        <label className="block text-xs font-medium text-secondary">{selectedPaymentMethod === 2 ? "Reference" : "Note"}</label>
                                                        <textarea
                                                            rows={4}
                                                            className="w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent"
                                                            value={reference}
                                                            placeholder={selectedPaymentMethod === 2 ? "Reference" : "Note"}
                                                            onChange={(e) => setReference(e.target.value)}
                                                            required
                                                        />
                                                    </div>

                                                    <button type="submit" className="w-[400px] mt-4 border bg-primary hover:bg-white hover:border-primary hover:text-primary text-white text-sm py-2 px-8 rounded-[70px] font-semibold transition shadow-md">
                                                        Pay
                                                    </button>
                                                </form>
                                            )
                                        }

                                    </div>

                                    <div className="flex mt-6">
                                    </div>
                                </div>
                            </div>
                        )
                    }
                    <div className="mt-2 p-2 w-[60%] border-b pb-2">
                        <div className="mt-3">
                            <span className="text-sm w-full inline-block font-semibold text-gray-800 border-b pb-2">
                                Payments
                            </span>

                            {paymentHistory.length === 0 ? (
                                <div className="text-sm text-gray-500 italic">
                                    No payment records available.
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm border-collapse">
                                        <thead className="bg-gray-50 text-gray-600">
                                            <tr>
                                                <th className="text-left px-4 py-3">Payment ID</th>
                                                <th className="text-right px-4 py-3">Amount</th>
                                                <th className="text-left px-4 py-3">Status</th>
                                                <th className="text-left px-4 py-3">Method</th>
                                                <th className="text-left px-4 py-3">Reference</th>
                                                <th className="text-left px-4 py-3">Created By</th>
                                                <th className="text-left px-4 py-3">Transaction ID</th>
                                                <th className="text-left px-4 py-3">Date</th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {paymentHistory.map((payment: any, index) => (
                                                <tr key={index} className="border-t hover:bg-gray-50">
                                                    <td className="px-4 py-3 font-medium">
                                                        {payment.paymentID ?? "--"}
                                                    </td>

                                                    <td className="px-4 py-3 text-right font-semibold">
                                                        ${payment.amount?.toFixed(2) || "0.00"}
                                                    </td>

                                                    <td className="px-4 py-3">
                                                        <StatusBadge status={payment.status} />
                                                    </td>

                                                    <td className="px-4 py-3">
                                                        {payment.paymentMethod || "--"}
                                                    </td>

                                                    <td className="px-4 py-3">
                                                        {payment.reference || "--"}
                                                    </td>

                                                    <td className="px-4 py-3">
                                                        {payment.createdBy || "--"}
                                                    </td>

                                                    <td className="px-4 py-3 text-gray-600">
                                                        {payment.transactionID || "--"}
                                                    </td>

                                                    <td className="px-4 py-3">
                                                        {payment.paymentDate
                                                            ? new Date(payment.paymentDate?.split("T")[0]).toLocaleDateString()
                                                            : "--"}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                    {
                        appointmentData?.status !== "Scheduled" && (
                            <button
                                type="button"
                                className="w-full sm:w-auto px-4 py-2 ml-2 mt-6 rounded bg-primary text-white cursor-pointer"
                                onClick={() => { goToNextPage() }}
                            >
                                Next
                            </button>
                        )
                    }

                </div>
            </div>
            <TerminalPleaseWaitPopup showPopup={terminalPleaseWaitPopup} onCancel={handleCancel} timeLeft={timeLeft} />
            <TerminalPaymentSuccessPopup showPopup={terminalPaymentSuccessPopup} />
        </>
    )
}


type SummaryRowProps = {
    label: string,
    value: number,
    bold?: boolean,
    highlight?: boolean
}

const SummaryRow = ({ label, value, bold, highlight }: SummaryRowProps) => (
    <div className="flex justify-between">
        <span className={`${bold ? "font-semibold" : ""}`}>
            {label}
        </span>
        <span
            className={`
        ${bold ? "font-semibold" : ""}
        ${highlight ? "text-red-600 font-bold" : ""}
      `}
        >
            ${value?.toFixed(2) || "0.00"}
        </span>
    </div>
);

type StatusBadgeProps = {
    status: string
}

type PaymentStatus = "approved" | "failed" | "pending";

const StatusBadge = ({ status }: StatusBadgeProps) => {
    const styles: Record<PaymentStatus, string> = {
        approved: "bg-green-100 text-green-700",
        failed: "bg-red-100 text-red-700",
        pending: "bg-yellow-100 text-yellow-700"
    };
    const normalizedStatus = status?.toLowerCase() as PaymentStatus;
    return (
        <span
            className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold
        ${styles[normalizedStatus] || "bg-gray-100 text-gray-600"}`}
        >
            {status || "Unknown"}
        </span>
    );
};